import React, { useEffect, useState } from 'react'
import { HTML5Backend } from 'react-dnd-html5-backend'
import { DndProvider } from 'react-dnd'
import { formatErrorMessages, type HttpResponse } from '../../../interfaces'
import { Alert, Snackbar, Stack } from '@mui/material'
import { DragAndDropItem } from './dragon-and-drop-item'

interface DragAndDropStackProps<T> {
  originalItems: T[]
  itemRenderer: (item: T, index: number) => React.ReactNode
  updateDisplayOrder: (items: T[], itemSelected: T) => Promise<HttpResponse<T[]>>
  disableDrag: boolean | ((item: T) => boolean)
  makeGray: boolean | ((item: T) => boolean)
  id: string
  suppressSuccessMessages?: boolean
  spacing?: number
}

export const DragAndDropStack: React.FC<DragAndDropStackProps<any>> = ({
  originalItems,
  updateDisplayOrder,
  itemRenderer,
  id,
  disableDrag,
  makeGray,
  suppressSuccessMessages = false,
  spacing = 2
}) => {
  const [currentItems, setCurrentItems] = useState<any[]>(originalItems)
  const [selectItem, setCurrentItem] = useState<any>(originalItems)
  const [oldItems, setOldItems] = useState<any[]>(originalItems)
  const [updating, setUpdating] = useState<boolean>(false)
  const [showSuccessAlert, setShowSuccessAlert] = useState(false)
  const [showUnsuccessAlert, setShowUnsuccessAlert] = useState(false)
  const [errorMessage, setErrorMessage] = useState('')
  const [isDragging, setIsDragging] = useState(false)

  useEffect(() => {
    setCurrentItems(originalItems)
  }, [originalItems])

  function handleSnackbarClose (_event: React.SyntheticEvent | Event, reason?: string): void {
    if (reason === 'clickaway') {
      return
    }

    setShowSuccessAlert(false)
    setShowUnsuccessAlert(false)
  }
  const startMove = (): void => {
    setOldItems([...currentItems])
  }

  const resetMove = (): void => {
    setCurrentItems([...oldItems])
  }

  const moveCard = (dragIndex: number, hoverIndex: number): void => {
    setCurrentItems(prevItems => {
      const newItems = [...prevItems]
      const draggedCard = newItems[dragIndex]
      setCurrentItem(draggedCard)
      newItems.splice(dragIndex, 1)
      newItems.splice(hoverIndex, 0, draggedCard)
      return newItems
    })
  }

  const dropCard = (): void => {
    const oldIdsOrder = oldItems.map(value => value.id)
    const currentIdsOrder = currentItems.map(value => value.id)
    if (JSON.stringify(oldIdsOrder) !== JSON.stringify(currentIdsOrder)) {
      setUpdating(true)
      updateDisplayOrder(currentItems, selectItem).then((value) => {
        if (value.succeeded) {
          if (!suppressSuccessMessages) {
            setShowSuccessAlert(true)
          }
          setCurrentItems(currentItems.map(item => (item.id === selectItem?.id) ? { ...item, lastUpdated: new Date().toISOString() } : item))
        } else {
          setErrorMessage(formatErrorMessages(value.errors))
          setShowUnsuccessAlert(true)
        }
      })
        .finally(() => { setUpdating(false) })
    }
  }

  return (
      <React.Fragment>
        {showSuccessAlert && (
            <Snackbar open={showSuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
              <Alert onClose={handleSnackbarClose} severity="success" sx={{ width: '100%' }}>
                The display order has been successfully updated
              </Alert>
            </Snackbar>
        )}
        {showUnsuccessAlert && (
            <Snackbar open={showUnsuccessAlert} autoHideDuration={2000} onClose={handleSnackbarClose}>
              <Alert onClose={handleSnackbarClose} severity="error" sx={{ width: '100%' }}>
                {errorMessage}
              </Alert>
            </Snackbar>
        )}
        <Stack spacing={spacing} sx={{ width: '100%', border: isDragging ? '2px dashed' : 'none', borderColor: isDragging ? 'primary.main' : 'none' }}>
        <DndProvider backend={HTML5Backend}>
        {currentItems.map((item, index) => (
            <DragAndDropItem
                key={index}
                index={index}
                updating={updating}
                startMoveCard={startMove}
                resetPositions={resetMove}
                setIsDragging={setIsDragging}
                moveCard={moveCard}
                dropCard={dropCard}
                stackId={id}
                disableDrag={typeof disableDrag === 'function' ? disableDrag(item) : disableDrag}
                makeGray={typeof makeGray === 'function' ? makeGray(item) : makeGray}
              >
              {itemRenderer(item, index)}
            </DragAndDropItem>
        ))}
      </DndProvider>
        </Stack>
      </React.Fragment>
  )
}
